// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Statement of Account page (Stage 6P.7).
//
// Route:  /erp/supplier-payments/history/:supplierId
//
// Read-only. Renders a supplier-wise statement of account by calling
// supplierPaymentApi.getSupplierHistory(supplierId, { date_from?,
// date_to?, limit? }) and interleaving the returned `bills` and
// `payments` arrays into a single date-sorted ledger.
//
// The backend (supplierPaymentReports.getSupplierPaymentHistory) is
// the SOLE authority for what counts as a bill / payment, the column
// shape (camelCase), and the per-row money values. This page never
// recomputes a balance, never invents a status, never mutates a row.
// It only:
//   - sorts the two arrays into a single interleaved timeline,
//   - rolls a running balance forward starting from openingPayable,
//   - renders identity + window summary in the header,
//   - renders the interleaved rows in an AntD Table.
//
// IMPORTANT — the backend currently STUBS opening payable, paid
// amount, balance and status for the per-bill section. The UI mirrors
// that reality with a small "preview" banner so operators don't read
// this as a live AR/AP statement. The shape stays stable across the
// later sub-stage that wires up the real supplier-payable view.
//
// Columns:
//   Date · Type (Bill / Payment) · Document No · Reference ·
//   Payable (+) / Paid (−) · Running Balance
//
// Style mirrors the SupplierPayablesPage shell (slate-50 header strip,
// 22 px row density, sticky toolbar) so the two screens read as a
// coherent set.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Empty,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  BankOutlined,
  FileTextOutlined,
  ReloadOutlined,
  WalletOutlined
} from '@ant-design/icons';

import { supplierPaymentApi } from './supplierPaymentApi';
import {
  normalizeSupplierPayment,
  paymentMethodLabel,
  paymentStatusColor,
  paymentStatusLabel
} from './supplierPaymentHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { Text } = Typography;
const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

const DATE_FMT = 'DD-MMM-YYYY';
const ISO_FMT  = 'YYYY-MM-DD';

const ROW_KIND_BILL    = 'BILL';
const ROW_KIND_PAYMENT = 'PAYMENT';

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// ₹ grouping is locale-aware (en-IN). Falsy / non-finite inputs
// collapse to an em-dash so empty cells don't read as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

// Always render the running balance — including the literal zero —
// so the timeline reads as a true ledger column.
function fmtMoneyZero(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// Compare two ISO/Date-ish values for ascending sort. Falsy / invalid
// values sort last (Number.POSITIVE_INFINITY) so a missing date never
// muddles the timeline.
function dateOrdinal(value) {
  if (!value) return Number.POSITIVE_INFINITY;
  const d = new Date(value);
  const t = d.getTime();
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
}


// ─────────────────────────────────────────────────────────────────
// Timeline builder — interleave bills + payments by date, then carry
// a running balance forward starting from openingPayable.
//
// Convention:
//   * A bill INCREASES the payable (we OWE the supplier more) → +.
//   * A payment DECREASES the payable (we PAID the supplier)   → −.
//   * Cancelled payments are excluded — the cancellation reverses the
//     ledger impact at the voucherPosting layer, so leaving them in
//     the timeline would double-count.
//
// Returns { rows, closingBalance, totals }.
// ─────────────────────────────────────────────────────────────────
function buildTimeline(historyShape) {
  const bills    = Array.isArray(historyShape?.bills)    ? historyShape.bills    : [];
  const payments = Array.isArray(historyShape?.payments) ? historyShape.payments : [];
  const openingPayable = toNumber(historyShape?.openingPayable);

  // Project bills onto the unified row shape.
  const billRows = bills.map((b) => ({
    kind:        ROW_KIND_BILL,
    date:        b?.billDate || null,
    documentNo:  b?.purchaseBillNo || null,
    reference:   b?.supplierInvoiceNo || null,
    referenceDate: b?.supplierInvoiceDate || null,
    amountIn:    toNumber(b?.grandTotal),
    amountOut:   0,
    raw:         b
  }));

  // Project payments — skip CANCELLED rows. Normalise once so
  // downstream consumers see canonical camelCase fields.
  const paymentRows = payments
    .map((p) => normalizeSupplierPayment(p))
    .filter((p) => p && p.status !== 'CANCELLED')
    .map((p) => ({
      kind:          ROW_KIND_PAYMENT,
      date:          p.paymentDate || null,
      documentNo:    p.paymentNo || null,
      reference:     p.instrumentNo || p.bankReference || null,
      referenceDate: p.instrumentDate || null,
      amountIn:      0,
      amountOut:     toNumber(p.paymentAmount),
      raw:           p
    }));

  // Sort ascending by date, with a secondary nudge so bills appear
  // before payments on the same date (a bill posted today must
  // increase the payable BEFORE any same-day payment knocks it down).
  const all = [...billRows, ...paymentRows].sort((a, b) => {
    const da = dateOrdinal(a.date);
    const db = dateOrdinal(b.date);
    if (da !== db) return da - db;
    if (a.kind === b.kind) return 0;
    return a.kind === ROW_KIND_BILL ? -1 : 1;
  });

  // Carry the running balance forward.
  let running = openingPayable;
  let totalIn = 0;
  let totalOut = 0;
  const rows = all.map((r, idx) => {
    running += r.amountIn - r.amountOut;
    totalIn  += r.amountIn;
    totalOut += r.amountOut;
    return {
      ...r,
      key:            `${r.kind}-${r.documentNo || idx}-${idx}`,
      runningBalance: running
    };
  });

  return {
    rows,
    openingPayable,
    closingBalance: running,
    totals: { in: totalIn, out: totalOut }
  };
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function SupplierPaymentHistoryPage() {
  const navigate    = useNavigate();
  const { supplierId } = useParams();
  const { me } = useAuth();

  // RBAC — the supplier statement lives under the reports-view slot
  // (mirrors the Stage 6P.7 RBAC contract; reports.view is the broad
  // read-only key, view is the per-payment list key, either grants
  // read access to this surface).
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView =
    hasPerm('erp.supplier_payments.reports.view') ||
    hasPerm('erp.supplier_payments.view');

  // ── Toolbar state ────────────────────────────────────────────
  const [dateRange, setDateRange] = useState(null);

  // ── Data state ───────────────────────────────────────────────
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    if (!supplierId) {
      message.error({
        content: 'Supplier id is missing from the URL.',
        key: 'sp-history'
      });
      return;
    }
    setLoading(true);
    try {
      const params = {};
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.date_from = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.date_to   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await supplierPaymentApi.getSupplierHistory(supplierId, params);
      setHistory(out && typeof out === 'object' ? out : null);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load supplier statement.'),
        key: 'sp-history'
      });
      setHistory(null);
    } finally {
      setLoading(false);
    }
  }, [canView, supplierId, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Derived: timeline + headline figures ─────────────────────
  const timeline = useMemo(() => buildTimeline(history), [history]);

  // Identity / window summary from the first available row. The
  // backend doesn't echo supplier name on this endpoint (the report
  // is keyed by id), so we lift it from the first payment row if
  // there is one. When neither bills nor payments exist we fall back
  // to the bare id.
  const supplierIdentity = useMemo(() => {
    const firstPayment = Array.isArray(history?.payments) && history.payments.length > 0
      ? normalizeSupplierPayment(history.payments[0])
      : null;
    return {
      supplierId:   history?.supplierId || supplierId || null,
      supplierName: firstPayment?.supplierName || null,
      supplierGstin: firstPayment?.supplierGstin || null
    };
  }, [history, supplierId]);

  // ── Toolbar handlers ─────────────────────────────────────────
  const onDates = (v) => setDateRange(v || null);
  const onReset = () => setDateRange(null);
  const onBack  = () => navigate(-1);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Date',
      dataIndex: 'date',
      width:     120,
      render: (v) => v
        ? <span className="grn-mono">{fmtDate(v)}</span>
        : <span className="grn-muted">—</span>
    },
    {
      title:     'Type',
      dataIndex: 'kind',
      width:     110,
      render: (v) => v === ROW_KIND_BILL
        ? <Tag color="warning" className="grn-status-tag">BILL</Tag>
        : <Tag color="processing" className="grn-status-tag">PAYMENT</Tag>
    },
    {
      title:     'Document No',
      dataIndex: 'documentNo',
      width:     180,
      render: (v) => v
        ? <span className="grn-mono">{v}</span>
        : <span className="grn-muted">—</span>
    },
    {
      title:     'Reference',
      dataIndex: 'reference',
      width:     220,
      render: (_v, row) => {
        const ref     = row.reference;
        const refDate = row.referenceDate;
        if (!ref && !refDate) return <span className="grn-muted">—</span>;
        // For payment rows surface the payment method as a tertiary
        // hint so the operator can see at a glance whether this was
        // bank/UPI/cheque/etc — the backend echoes payment_method on
        // the normalized payment row.
        const method = row.kind === ROW_KIND_PAYMENT && row.raw?.paymentMethod
          ? paymentMethodLabel(row.raw.paymentMethod)
          : null;
        return (
          <div className="grn-supplier">
            <span className="grn-mono" style={{ fontSize: 12 }}>
              {ref || '—'}
            </span>
            <span className="grn-muted" style={{ fontSize: 10.5 }}>
              {refDate ? fmtDate(refDate) : ''}
              {refDate && method ? ' · ' : ''}
              {method || ''}
            </span>
          </div>
        );
      }
    },
    {
      title:     'Payable (+)',
      dataIndex: 'amountIn',
      width:     130,
      align:     'right',
      render: (v) => v
        ? <span className="grn-mono grn-line-count" style={{ color: '#b45309' }}>
            +{fmtMoney(v)}
          </span>
        : <span className="grn-muted">—</span>
    },
    {
      title:     'Paid (−)',
      dataIndex: 'amountOut',
      width:     130,
      align:     'right',
      render: (v) => v
        ? <span className="grn-mono grn-line-count" style={{ color: '#15803d' }}>
            −{fmtMoney(v)}
          </span>
        : <span className="grn-muted">—</span>
    },
    {
      title:     'Running Balance',
      dataIndex: 'runningBalance',
      width:     150,
      align:     'right',
      render: (v) => {
        const n = toNumber(v);
        const colour = n > 0 ? '#0f172a' : (n < 0 ? '#15803d' : '#64748b');
        return (
          <span className="grn-mono grn-line-count" style={{ color: colour, fontWeight: 600 }}>
            {fmtMoneyZero(v)}
          </span>
        );
      }
    },
    // Tail column — payment posting status badge (only meaningful on
    // payment rows; blank for bills). Lives at the end so the money
    // columns stay visually grouped to the left of it.
    {
      title:     'Status',
      dataIndex: 'status',
      width:     130,
      render: (_v, row) => {
        if (row.kind === ROW_KIND_BILL) {
          const billStatus = row.raw?.status || 'UNPAID';
          return <Tag color="warning" className="grn-status-tag">{billStatus}</Tag>;
        }
        const s = row.raw?.status || '';
        return s
          ? <Tag color={paymentStatusColor(s)} className="grn-status-tag">
              {paymentStatusLabel(s)}
            </Tag>
          : <span className="grn-muted">—</span>;
      }
    }
  ]), []);

  // ── Render: permission gate ──────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view the supplier statement."
          description="Ask your master account to grant erp.supplier_payments.reports.view or erp.supplier_payments.view."
        />
      </div>
    );
  }

  if (!supplierId) {
    return (
      <div className="grn-shell">
        <Alert
          type="error"
          showIcon
          message="Supplier id is missing from the URL."
          description="This page expects /erp/supplier-payments/history/:supplierId."
        />
      </div>
    );
  }

  // ── Render: page body ────────────────────────────────────────
  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><WalletOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Supplier Statement of Account</span>
            <span className="grn-headstrip__subtitle">
              Interleaved timeline of posted Purchase Bills (payable +)
              and Supplier Payments (paid −) for this supplier. Running
              balance starts at the opening payable carry-forward.
              Read-only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <Button
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
          >
            Back
          </Button>
          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={load}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Preview-data banner — the backend currently stubs paid /
          balance / status on the per-bill section, and opening
          payable carry-forward. Surface that explicitly so operators
          don't read this as a finalised statement. */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 8 }}
        message="Statement preview"
        description="Opening payable carry-forward and per-bill paid / balance figures are backend placeholders. Posted payments are real and feed the running balance below. The supplier-payable balance roll-up engine ships in a later sub-stage; this surface stays stable across that upgrade."
      />

      {/* Identity + statement summary card */}
      <Card
        className="grn-card"
        bordered={false}
        bodyStyle={{ padding: 12 }}
        style={{ marginBottom: 8 }}
      >
        <Spin spinning={loading}>
          <div style={{
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            justifyContent: 'space-between'
          }}>
            <div style={{ flex: '1 1 360px', minWidth: 280 }}>
              <Descriptions
                size="small"
                column={1}
                colon={false}
                labelStyle={{ color: '#64748b', fontSize: 11.5, width: 120 }}
                contentStyle={{ color: '#0f172a', fontSize: 12.5 }}
              >
                <Descriptions.Item label={<><BankOutlined /> Supplier</>}>
                  <Text strong>{supplierIdentity.supplierName || '—'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Supplier ID">
                  <span className="grn-mono" style={{ fontSize: 11.5 }}>
                    {supplierIdentity.supplierId || '—'}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="GSTIN">
                  <span className="grn-mono" style={{ fontSize: 11.5 }}>
                    {supplierIdentity.supplierGstin || '—'}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="Window">
                  <span className="grn-mono" style={{ fontSize: 11.5 }}>
                    {history?.dateFrom ? fmtDate(history.dateFrom) : '—'}
                    {' → '}
                    {history?.dateTo   ? fmtDate(history.dateTo)   : '—'}
                  </span>
                </Descriptions.Item>
              </Descriptions>
            </div>

            <div style={{
              display: 'flex',
              gap: 24,
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <Statistic
                title="Opening Payable"
                value={fmtMoneyZero(timeline.openingPayable)}
                prefix="₹"
                valueStyle={{ fontSize: 16, color: '#64748b' }}
              />
              <Statistic
                title="Total Payable (+)"
                value={fmtMoneyZero(timeline.totals.in)}
                prefix="₹"
                valueStyle={{ fontSize: 16, color: '#b45309' }}
              />
              <Statistic
                title="Total Paid (−)"
                value={fmtMoneyZero(timeline.totals.out)}
                prefix="₹"
                valueStyle={{ fontSize: 16, color: '#15803d' }}
              />
              <Statistic
                title="Closing Balance"
                value={fmtMoneyZero(timeline.closingBalance)}
                prefix="₹"
                valueStyle={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: timeline.closingBalance > 0
                    ? '#0f172a'
                    : (timeline.closingBalance < 0 ? '#15803d' : '#64748b')
                }}
              />
            </div>
          </div>
        </Spin>
      </Card>

      {/* Sticky toolbar — date window picker. The backend honours
          date_from / date_to as inclusive bounds over both bills and
          payments; clearing the picker shows the default window. */}
      <div className="grn-toolbar">
        <FileTextOutlined style={{ color: '#94a3b8' }} />
        <span style={{ fontSize: 11.5, color: '#475569' }}>Statement window</span>
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 260 }}
        />
        <Tooltip title="Clear window">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <span className="grn-stat-chip">
          <strong>{timeline.rows.length}</strong> rows
        </span>
      </div>

      {/* Timeline grid */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey="key"
            dataSource={timeline.rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            pagination={false}
            locale={{
              emptyText: loading
                ? ' '
                : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No bills or payments in this window."
                  />
                )
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
